import { useCallback, useEffect, useState } from 'react';
import { buildApiUrl } from '../services/api';

export interface RuntimeCapabilities {
  runtime: {
    websocketEnabled: boolean;
    websocketInitialized: boolean;
    campaignSchedulerEnabled: boolean;
    alertsMonitoringEnabled: boolean;
    automationTimeTriggersEnabled: boolean;
    campaignRetentionEnabled: boolean;
  };
  externalSchedulers: {
    automationTimeTriggers: boolean;
    campaignRetention: boolean;
  };
  realtime: {
    transport: 'socket.io' | 'polling';
    pollingFallbackRequired: boolean;
    inboxUpdatesViaPolling: boolean;
    notificationsViaPolling: boolean;
    campaignProgressViaPolling: boolean;
  };
  recommendations: {
    notificationsPollingIntervalMs: number;
    inboxPollingIntervalMs: number;
    campaignsPollingIntervalMs: number;
  };
}

const DEFAULT_RUNTIME_CAPABILITIES: RuntimeCapabilities = {
  runtime: {
    websocketEnabled: false,
    websocketInitialized: false,
    campaignSchedulerEnabled: false,
    alertsMonitoringEnabled: false,
    automationTimeTriggersEnabled: false,
    campaignRetentionEnabled: false,
  },
  externalSchedulers: {
    automationTimeTriggers: false,
    campaignRetention: false,
  },
  realtime: {
    transport: 'polling',
    pollingFallbackRequired: true,
    inboxUpdatesViaPolling: true,
    notificationsViaPolling: true,
    campaignProgressViaPolling: true,
  },
  recommendations: {
    notificationsPollingIntervalMs: 15000,
    inboxPollingIntervalMs: 5000,
    campaignsPollingIntervalMs: 10000,
  },
};

export function useRuntimeCapabilities(selectedTenantId?: string | null) {
  const [capabilities, setCapabilities] = useState<RuntimeCapabilities>(DEFAULT_RUNTIME_CAPABILITIES);
  const [loading, setLoading] = useState(true);

  const loadRuntimeCapabilities = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      setCapabilities(DEFAULT_RUNTIME_CAPABILITIES);
      return;
    }

    try {
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (selectedTenantId) {
        (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
      }

      const response = await fetch(buildApiUrl('/system/runtime-capabilities'), {
        headers,
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setCapabilities({
        ...DEFAULT_RUNTIME_CAPABILITIES,
        ...data,
        runtime: {
          ...DEFAULT_RUNTIME_CAPABILITIES.runtime,
          ...(data?.runtime || {}),
        },
        externalSchedulers: {
          ...DEFAULT_RUNTIME_CAPABILITIES.externalSchedulers,
          ...(data?.externalSchedulers || {}),
        },
        realtime: {
          ...DEFAULT_RUNTIME_CAPABILITIES.realtime,
          ...(data?.realtime || {}),
        },
        recommendations: {
          ...DEFAULT_RUNTIME_CAPABILITIES.recommendations,
          ...(data?.recommendations || {}),
        },
      });
    } catch (error) {
      console.error('Erro ao carregar runtime capabilities:', error);
      setCapabilities(DEFAULT_RUNTIME_CAPABILITIES);
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    void loadRuntimeCapabilities();
  }, [loadRuntimeCapabilities]);

  return {
    capabilities,
    loading,
    refetch: loadRuntimeCapabilities,
  };
}
